import React, { type CSSProperties } from 'react';

type MagneticHandleButtonProps = {
  followPointer?: boolean;
  side: 'left' | 'right';
  visible: boolean;
  x: number;
  y: number;
  showPlus?: boolean;
};

const HANDLE_BUTTON_SIZE = 24;
const HANDLE_BUTTON_HALF_SIZE = HANDLE_BUTTON_SIZE / 2;

function resolveHandleStyle({
  followPointer,
  x,
  y,
}: Pick<MagneticHandleButtonProps, 'followPointer' | 'x' | 'y'>): CSSProperties {
  const movementDuration = followPointer ? '300ms' : '220ms';
  const movementEasing = followPointer ? 'cubic-bezier(0.18, 0.9, 0.24, 1)' : 'cubic-bezier(0.34, 1.56, 0.64, 1)';

  return {
    transform: `translate(${x - HANDLE_BUTTON_HALF_SIZE}px, ${y - HANDLE_BUTTON_HALF_SIZE}px)`,
    transition: [
      `transform ${movementDuration} ${movementEasing}`,
      'opacity 220ms ease',
    ].join(', '),
  } as CSSProperties;
}

export const HandleButton = React.memo(function HandleButton({
  followPointer = false,
  visible,
  x,
  y,
  showPlus = true,
}: MagneticHandleButtonProps) {
  const [shouldRender, setShouldRender] = React.useState(visible);

  React.useEffect(() => {
    if (visible) {
      setShouldRender(true);
    }
  }, [visible]);

  const handleTransitionEnd = React.useCallback((event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'opacity' || visible) {
      return;
    }

    setShouldRender(false);
  }, [visible]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={[
        'pointer-events-none absolute left-0 top-0 h-[24px] w-[24px] rounded-full text-[#7A7A7A]',
        showPlus ? 'border border-[#7A7A7A] border-2 bg-[rgba(22,23,26,0.8)]' : '',
        showPlus ? 'before:absolute before:left-1/2 before:top-1/2 before:h-[10px] before:w-[2px] before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-current before:content-[""]' : '',
        showPlus ? 'after:absolute after:left-1/2 after:top-1/2 after:h-[2px] after:w-[10px] after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-current after:content-[""]' : '',
        visible ? 'opacity-100' : 'opacity-0',
      ].join(' ')}
      style={resolveHandleStyle({
        followPointer,
        x,
        y,
      })}
      onTransitionEnd={handleTransitionEnd}
      aria-hidden="true"
    />
  );
});
